import React, { useState, useEffect } from 'react';
import { 
  X, 
  Download,
  RefreshCw
} from 'lucide-react';
import { CountBadge } from './CountBadge';
import { FilterButton } from './FilterButton';
import DebouncedSearchInput from './DebouncedSearchInput';
import DatePickerField from './DatePickerField';
import PillSelect from './PillSelect';
import { parseLocalDate, toLocalISODate } from '../../utils/dateUtils';

const AdvancedFilters = ({ 
  onFiltersChange, 
  onExport, 
  filterConfig = {},
  loading = false,
  resultCount = 0,
  theme = 'employee'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [filters, setFilters] = useState({});
  const [searchTerm, setSearchTerm] = useState('');

  // Apply filters when they change
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      onFiltersChange({ ...filters, search: searchTerm });
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [filters, searchTerm, onFiltersChange]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const clearFilters = () => {
    setFilters({});
    setSearchTerm('');
  };

  const activeFilterCount = Object.keys(filters).filter(key => 
    filters[key] && filters[key] !== '' && 
    (Array.isArray(filters[key]) ? filters[key].length > 0 : true)
  ).length + (searchTerm ? 1 : 0);

  const clearFilterKey = (key) => {
    setFilters(prev => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const getChoiceLabel = (key, value) => {
    const cfg = (filterConfig.choices || []).find(c => c.key === key);
    if (!cfg) return value;
    const opt = (cfg.options || []).find(o => String(o.value) === String(value));
    return opt ? opt.label : value;
  };

  const buildActiveChips = () => {
    const chips = [];
    // Search term
    if (searchTerm) {
      chips.push({ id: 'search', label: `Search: ${searchTerm}`, onClear: () => setSearchTerm('') });
    }
    // Date range
    if (filterConfig.dateRange) {
      const k = filterConfig.dateRange.key;
      const fromV = filters[`${k}_from`];
      const toV = filters[`${k}_to`];
      if (fromV || toV) {
        if (fromV && toV) {
          chips.push({ id: `${k}_range`, label: `${filterConfig.dateRange.label}: ${fromV} – ${toV}`,
            onClear: () => { clearFilterKey(`${k}_from`); clearFilterKey(`${k}_to`); } });
        } else if (fromV) {
          chips.push({ id: `${k}_from`, label: `${filterConfig.dateRange.label} ≥ ${fromV}`, onClear: () => clearFilterKey(`${k}_from`) });
        } else if (toV) {
          chips.push({ id: `${k}_to`, label: `${filterConfig.dateRange.label} ≤ ${toV}`, onClear: () => clearFilterKey(`${k}_to`) });
        }
      }
    }
    // Choices
    (filterConfig.choices || []).forEach(choice => {
      const v = filters[choice.key];
      if (v && v !== '') {
        chips.push({ id: choice.key, label: `${choice.label}: ${getChoiceLabel(choice.key, v)}`, onClear: () => clearFilterKey(choice.key) });
      }
    });
    // Booleans
    (filterConfig.booleans || []).forEach(boolean => {
      const v = filters[boolean.key];
      if (v === 'true' || v === 'false') {
        chips.push({ id: boolean.key, label: `${boolean.label}: ${v === 'true' ? 'Yes' : 'No'}`, onClear: () => clearFilterKey(boolean.key) });
      }
    });
    // Numeric ranges
    (filterConfig.numericRanges || []).forEach(range => {
      const minV = filters[`${range.key}_min`];
      const maxV = filters[`${range.key}_max`];
      if (minV || maxV) {
        if (minV && maxV) {
          chips.push({ id: `${range.key}_both`, label: `${range.label}: ${minV}–${maxV}`, onClear: () => { clearFilterKey(`${range.key}_min`); clearFilterKey(`${range.key}_max`); } });
        } else if (minV) {
          chips.push({ id: `${range.key}_min`, label: `${range.label} ≥ ${minV}`, onClear: () => clearFilterKey(`${range.key}_min`) });
        } else if (maxV) {
          chips.push({ id: `${range.key}_max`, label: `${range.label} ≤ ${maxV}`, onClear: () => clearFilterKey(`${range.key}_max`) });
        }
      }
    });
    return chips;
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      {/* Header */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            {/* Search Bar */}
            <DebouncedSearchInput
              value={searchTerm}
              onSearch={(val) => setSearchTerm(val)}
              placeholder="Search..."
              theme={theme}
              className="flex-1 min-w-64"
            />
          </div>

          <div className="flex items-center space-x-2">
            {/* Filter Toggle moved to right */}
            <FilterButton
              theme={theme}
              isOpen={isOpen}
              activeCount={activeFilterCount}
              onClick={() => setIsOpen(!isOpen)}
            />

            {/* Action Buttons */}
            {onExport && (
              <button
                onClick={onExport}
                disabled={resultCount === 0}
                className="icon-button disabled:opacity-50 disabled:cursor-not-allowed"
                title="Export"
                aria-label="Export"
              >
                <Download className="w-4 h-4" />
              </button>
            )}

            {activeFilterCount > 0 && (
              <button
                onClick={clearFilters}
                className="icon-button"
                title="Clear Filters"
                aria-label="Clear Filters"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
        {/* Active filter chips */}
        {activeFilterCount > 0 && (
          <div className="flex flex-wrap items-center gap-2 mt-3">
            {buildActiveChips().map(chip => (
              <span key={chip.id} className="pill pill-secondary inline-flex items-center">
                {chip.label}
                <button
                  onClick={chip.onClear}
                  className="ml-2 inline-flex items-center justify-center rounded-full w-5 h-5 text-gray-600 hover:text-gray-800"
                  title="Remove filter"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
            <button onClick={clearFilters} className="btn btn-outline btn-sm ml-1">
              Clear all
            </button>
          </div>
        )}
      </div>

      {/* Expanded Filter Panel */}
      {isOpen && (
        <div className="p-4 border-b border-gray-200 bg-gray-50">
          {/* Panel actions */}
          <div className="flex items-center justify-end mb-4">
            {activeFilterCount > 0 && (
              <button onClick={clearFilters} className="btn btn-outline btn-sm">
                <X className="w-4 h-4 mr-2" />
                Clear Filters
              </button>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Date Range Filter */}
            {filterConfig.dateRange && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {filterConfig.dateRange.label}
                </label>
                <div className="flex space-x-2">
                  <div className="flex-1">
                    <DatePickerField
                      theme={theme}
                      selected={parseLocalDate(filters[`${filterConfig.dateRange.key}_from`])}
                      onChange={(date) => handleFilterChange(`${filterConfig.dateRange.key}_from`, toLocalISODate(date))}
                      placeholderText="From"
                      isClearable
                      maxDate={parseLocalDate(filters[`${filterConfig.dateRange.key}_to`])}
                    />
                  </div>
                  <div className="flex-1">
                    <DatePickerField
                      theme={theme}
                      selected={parseLocalDate(filters[`${filterConfig.dateRange.key}_to`])}
                      onChange={(date) => handleFilterChange(`${filterConfig.dateRange.key}_to`, toLocalISODate(date))}
                      placeholderText="To"
                      isClearable
                      minDate={parseLocalDate(filters[`${filterConfig.dateRange.key}_from`])}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Choice Filters */}
            {filterConfig.choices && filterConfig.choices.map((choice, index) => (
              <div key={index}>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {choice.label}
                </label>
                <PillSelect
                  value={filters[choice.key] || ''}
                  onChange={(val) => handleFilterChange(choice.key, val)}
                  options={[
                    { value: '', label: `All ${choice.label}` },
                    ...choice.options.map(o => ({ value: String(o.value), label: o.label }))
                  ]}
                  theme={theme}
                />
              </div>
            ))}

            {/* Boolean Filters */}
            {filterConfig.booleans && filterConfig.booleans.map((boolean, index) => (
              <div key={index}>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {boolean.label}
                </label>
                {/* Segmented pill toggle: All | Yes | No */}
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => handleFilterChange(boolean.key, '')}
                    className={`btn btn-sm ${
                      filters[boolean.key] === '' || filters[boolean.key] === undefined
                        ? 'btn-primary'
                        : 'btn-outline'
                    }`}
                  >
                    All
                  </button>
                  <button
                    type="button"
                    onClick={() => handleFilterChange(boolean.key, 'true')}
                    className={`btn btn-sm ${
                      filters[boolean.key] === 'true' ? 'btn-primary' : 'btn-outline'
                    }`}
                  >
                    Yes
                  </button>
                  <button
                    type="button"
                    onClick={() => handleFilterChange(boolean.key, 'false')}
                    className={`btn btn-sm ${
                      filters[boolean.key] === 'false' ? 'btn-primary' : 'btn-outline'
                    }`}
                  >
                    No
                  </button>
                </div>
              </div>
            ))}

            {/* Numeric Range Filters */}
            {filterConfig.numericRanges && filterConfig.numericRanges.map((range, index) => (
              <div key={index}>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {range.label}
                </label>
                <div className="flex space-x-2">
                  <input
                    type="number"
                    placeholder="Min"
                    value={filters[`${range.key}_min`] || ''}
                    onChange={(e) => handleFilterChange(`${range.key}_min`, e.target.value)}
                    className="input flex-1"
                  />
                  <input
                    type="number"
                    placeholder="Max"
                    value={filters[`${range.key}_max`] || ''}
                    onChange={(e) => handleFilterChange(`${range.key}_max`, e.target.value)}
                    className="input flex-1"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdvancedFilters; 